import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import config from '../../config.json';
import '../features/features.css';
import './PricingPage.css';

const RATE = 0.015; // 1.5%
const CAP = 2000; // ₦2,000 maximum fee per transaction
const CAP_REACHED_AT = CAP / RATE; // ₦133,333.33

const METHODS = ['Dynamic virtual account', 'Bank transfer', 'Card', 'USSD', 'PayAttitude'];
const EXAMPLES = [1000, 5000, 20000, 50000, 133334, 500000];

const naira = new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

export const feeFor = (amount) => Math.min(Math.round(amount * RATE * 100) / 100, CAP);

export function Calculator() {
    const [raw, setRaw] = useState('50000');
    const amount = Number(raw) || 0;
    const { fee, net, capped } = useMemo(() => {
        const f = feeFor(amount);
        return { fee: f, net: Math.max(amount - f, 0), capped: amount >= CAP_REACHED_AT };
    }, [amount]);

    const onChange = (e) => {
        // Digits and one decimal point only.
        const v = e.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
        setRaw(v);
    };

    return (
        <div className="wqp-calc">
            <label htmlFor="wqp-amount" className="wqp-calc-label">
                If your customer pays
            </label>
            <div className="wqp-input">
                <span aria-hidden="true">₦</span>
                <input
                    id="wqp-amount"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={raw}
                    onChange={onChange}
                    aria-describedby="wqp-calc-result"
                />
            </div>
            <dl id="wqp-calc-result" className="wqp-calc-result" aria-live="polite">
                <div>
                    <dt>Our fee</dt>
                    <dd>{naira.format(fee)}</dd>
                </div>
                <div>
                    <dt>You receive</dt>
                    <dd className="wqp-net">{naira.format(net)}</dd>
                </div>
            </dl>
            <p className="wqf-small wqp-calc-note">
                {capped
                    ? 'This payment has reached the ₦2,000 cap, so the fee stays at ₦2,000 however large it gets.'
                    : `The fee reaches the ₦2,000 cap at ${naira.format(Math.ceil(CAP_REACHED_AT))}.`}
            </p>
        </div>
    );
}

function PricingPage() {
    return (
        <div className="wqf wqp">
            {/* Kept on bg-primary-theme so it runs straight on from the navbar. */}
            <header className="bg-primary-theme wqp-band">
                <div className="wqf-wrap">
                    <p className="wqp-region">Pricing for Nigeria</p>
                    <h1 className="wqf-h1 wqp-title">One simple rate for every payment</h1>
                    <p className="wqp-sub">
                        1.5% per transaction, capped at ₦2,000 — whichever way your customer pays. No surprises, no
                        hidden charges.
                    </p>
                </div>
            </header>

            <div className="wqf-wrap">
                <section className="wqp-card" aria-labelledby="wqp-rate">
                    <div className="wqp-rate">
                        <p id="wqp-rate" className="wqp-rate-figure">
                            1.5%
                        </p>
                        <p className="wqp-rate-label">per transaction</p>
                        <p className="wqp-cap">
                            Capped at <strong>₦2,000</strong>. That's the most you'll ever pay on a single payment.
                        </p>
                        <h2 className="wqf-h4 wqp-methods-title">Same rate for all payment methods</h2>
                        <ul className="wqp-methods">
                            {METHODS.map((m) => (
                                <li key={m}>{m}</li>
                            ))}
                        </ul>
                        <a href={`${config.CUSTOMER_WEBSITE_URL}/register`} className="wqf-btn wqf-btn-primary wqp-cta">
                            Create an account
                        </a>
                    </div>
                    <Calculator />
                </section>
            </div>

            <section className="wqp-section" aria-labelledby="wqp-examples-title">
                <div className="wqf-wrap wqp-two">
                    <div>
                        <h2 id="wqp-examples-title" className="wqf-h2">
                            What you pay on common amounts
                        </h2>
                        <p className="wqf-lead">
                            The fee is 1.5% of the payment until it reaches ₦2,000. From ₦133,334 upwards, it never
                            goes higher.
                        </p>
                    </div>
                    <table className="wqp-table">
                        <thead>
                            <tr>
                                <th scope="col">Customer pays</th>
                                <th scope="col">Fee</th>
                                <th scope="col">You receive</th>
                            </tr>
                        </thead>
                        <tbody>
                            {EXAMPLES.map((a) => {
                                const fee = feeFor(a);
                                return (
                                    <tr key={a} className={fee === CAP ? 'wqp-capped' : undefined}>
                                        <td>{naira.format(a)}</td>
                                        <td>
                                            {naira.format(fee)}
                                            {fee === CAP && <span className="wqp-tag">Cap</span>}
                                        </td>
                                        <td>{naira.format(a - fee)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="wqp-section wqp-section-last" aria-labelledby="wqp-faq-title">
                <div className="wqf-wrap">
                    <h2 id="wqp-faq-title" className="wqf-h2">
                        Good to know
                    </h2>
                    <ul className="wqf-items wqp-faq">
                        <li>
                            <h3 className="wqf-h4">Same price, any method</h3>
                            <p className="wqf-small">
                                Dynamic virtual account, bank transfer, card, USSD and PayAttitude all cost 1.5%,
                                capped at ₦2,000.
                            </p>
                        </li>
                        <li>
                            <h3 className="wqf-h4">Fees come off each payment</h3>
                            <p className="wqf-small">
                                You're settled the amount after fees, and your dashboard shows both your gross and net
                                revenue.
                            </p>
                        </li>
                        <li>
                            <h3 className="wqf-h4">Testing is free</h3>
                            <p className="wqf-small">
                                Test mode uses test money, so you can try every feature before you go live without
                                paying anything.
                            </p>
                        </li>
                        <li>
                            <h3 className="wqf-h4">High volumes?</h3>
                            <p className="wqf-small">
                                If you process large volumes, <Link to="/contact" className="wqp-inline">talk to us</Link>{' '}
                                about pricing for your business.
                            </p>
                        </li>
                    </ul>
                </div>
            </section>
        </div>
    );
}

export default PricingPage;
